import React, { useEffect } from "react";
import { Box, Button, useTheme } from "@mui/material";
import KitModal from "../../../UI/KitModal";
import { useDarkMode } from "../../../../hooks/useDarkMode";
import { useQueryClient } from "@tanstack/react-query";
import { vectorQueries } from "../../../../queries/vector";
import { spacesQueries } from "../../../../queries/spaces";
import { AuthService } from "../../../../service/AuthService";
import { useAuthStore } from "../../../../store/storeAuth";
import { useObjectStore } from "../../../../store/storeObjects";
import { VectorMemoryModal } from "../../../UI/VectorMemoryModal";
import ObjectModal from "../../../Layout/LayoutComponents/FluxObjects/ObjectModal";
import CreateFormModal from "../../../Forms/CreateFormModal";
import { CreateItemType } from "../types";
import { useCreateItemModal } from "./hooks/useCreateItemModal";
import { useItemHandlers } from "./handlers/useItemHandlers";
import { AgentForm } from "./components/AgentForm";
import { FluxbotForm } from "./components/FluxbotForm";
import { VectorMemoryForm } from "./components/VectorMemoryForm";

interface CreateItemModalProps {
  show: boolean;
  onHide: () => void;
  type: CreateItemType;
}

const getTitle = (type: CreateItemType): string => {
  switch (type) {
    case "agent":
      return "Create New Agent";
    case "interface":
      return "Create New Interface";
    case "fluxbot":
      return "Create New Fluxbot";
    case "object":
      return "Create New Object";
    case "vectorMemory":
      return "Create New Vector Memory";
    default:
      return "Create New Item";
  }
};

export const CreateItemModal: React.FC<CreateItemModalProps> = ({
  show,
  onHide,
  type,
}) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const queryClient = useQueryClient();
  const { setAuthUser } = useAuthStore((state) => state);
  const {
    getObjectsBySpaceId,
    resetObjectUpdate,
    removeObject,
    setObjectUpdate,
  } = useObjectStore((state) => state);

  const {
    name,
    setName,
    selectedSpaceId,
    setSelectedSpaceId,
    spaces,
    isCreating,
    setIsCreating,
    showVectorModal,
    setShowVectorModal,
    showObjectModal,
    setShowObjectModal,
    createdObjectSpaceId,
    vectorMemorySpaceId,
    setVectorMemorySpaceId,
    showInterfaceModal,
    setShowInterfaceModal,
    isCreateFormLoading,
    setIsCreateFormLoading,
    ensureFolderExists,
    loadSpaces,
    resetForm,
  } = useCreateItemModal(type, onHide);

  const { handleCreate, handleCreateInterface } = useItemHandlers({
    type,
    name,
    selectedSpaceId,
    ensureFolderExists,
    loadSpaces,
    resetForm,
    onHide,
    setVectorMemorySpaceId,
    setShowVectorModal,
    setIsCreating,
    setIsCreateFormLoading,
    setShowInterfaceModal,
  });

  useEffect(() => {
    if (show) {
      setShowVectorModal(false);
      setVectorMemorySpaceId(undefined);

      if (type === "object") {
        const newObject = {
          id: Math.random().toString(36).substr(2, 9),
          name: "",
          text: [{ id: Math.random().toString(36).substr(2, 9), text: "" }],
          isCreating: true,
          localObject: false,
          sessionEnabled: false,
        };
        setObjectUpdate(newObject);
        setShowObjectModal(true);
      }

      if (type === "fluxbot") {
        setSelectedSpaceId("private");
      }
    }
  }, [
    show,
    type,
    setShowVectorModal,
    setVectorMemorySpaceId,
    setShowObjectModal,
    setSelectedSpaceId,
  ]);

  useEffect(() => {
    if (show && type === "interface") {
      setShowInterfaceModal(true);
    } else if (!show) {
      setShowInterfaceModal(false);
    }
  }, [show, type, setShowInterfaceModal]);

  if (type === "interface" && showInterfaceModal) {
    return (
      <CreateFormModal
        show={showInterfaceModal}
        onHide={() => {
          setShowInterfaceModal(false);
          onHide();
        }}
        isCreateFormLoading={isCreateFormLoading}
        onCreateForm={handleCreateInterface}
        selectedSpaceId={selectedSpaceId === "private" ? "" : selectedSpaceId}
        onSpaceChange={(spaceId) => setSelectedSpaceId(spaceId || "private")}
        showSpaceSelector={true}
      />
    );
  }

  if (type === "object" && showObjectModal) {
    return (
      <ObjectModal
        show={showObjectModal}
        onHide={async () => {
          setShowObjectModal(false);
          const currentObjects = getObjectsBySpaceId(
            createdObjectSpaceId || "user",
          );
          const creatingObject = currentObjects.find((obj) => obj.isCreating);
          if (creatingObject) {
            removeObject(creatingObject.id, createdObjectSpaceId || "user");
          }
          resetObjectUpdate();
          await new Promise((resolve) => setTimeout(resolve, 100));
          window.dispatchEvent(new CustomEvent("sidebar-refresh"));
          onHide();
        }}
        spaceId={createdObjectSpaceId}
      />
    );
  }

  if (type === "vectorMemory" && showVectorModal) {
    return (
      <VectorMemoryModal
        spaceId={vectorMemorySpaceId}
        open={showVectorModal}
        onOpenChange={async (state) => {
          if (!state.open) {
            setShowVectorModal(false);
            setVectorMemorySpaceId(undefined);

            if (vectorMemorySpaceId) {
              await queryClient.invalidateQueries({
                queryKey: spacesQueries.all().queryKey,
              });
            } else {
              await queryClient.invalidateQueries({
                queryKey: vectorQueries.allNamespaces().queryKey,
              });
              const userRes = await AuthService.getMe();
              setAuthUser(userRes?.data);
            }

            window.dispatchEvent(new CustomEvent("sidebar-refresh"));
            onHide();
          }
        }}
      />
    );
  }

  const renderForm = () => {
    switch (type) {
      case "agent":
        return (
          <AgentForm
            name={name}
            onNameChange={setName}
            selectedSpaceId={selectedSpaceId}
            onSpaceChange={setSelectedSpaceId}
            spaces={spaces}
            disabled={isCreating}
          />
        );
      case "fluxbot":
        return (
          <FluxbotForm
            name={name}
            onNameChange={setName}
            selectedSpaceId={selectedSpaceId}
            onSpaceChange={setSelectedSpaceId}
            spaces={spaces}
            disabled={isCreating}
          />
        );
      case "vectorMemory":
        return (
          <VectorMemoryForm
            selectedSpaceId={selectedSpaceId}
            onSpaceChange={setSelectedSpaceId}
            spaces={spaces}
            disabled={isCreating}
          />
        );
      default:
        return null;
    }
  };

  const isFormValid = () => {
    if (type === "agent" || type === "fluxbot") {
      return name.trim().length > 0;
    }
    return true;
  };

  return (
    <KitModal
      show={
        show && type !== "interface" && type !== "object" && !showVectorModal
      }
      onHide={onHide}
      title={getTitle(type)}
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          maxWidth: "520px",
        },
      }}
      footer={
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={onHide}
            disabled={isCreating}
            sx={{
              color: isDarkMode ? palette.gray[300] : palette.gray[700],
              borderColor: isDarkMode ? palette.gray[700] : palette.gray[300],
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={isCreating || !isFormValid()}
            sx={{
              backgroundColor: palette.blue[500],
              "&:hover": {
                backgroundColor: palette.blue[600],
              },
            }}
          >
            {isCreating ? "Creating..." : "Create"}
          </Button>
        </Box>
      }
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 3, padding: 2 }}
      >
        {renderForm()}
      </Box>
    </KitModal>
  );
};
